import React from 'react';
import { Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import * as Icons from '@ant-design/icons';

export interface CategoryInfo {
  id: string;
  categoryLabel: string;
  color?: string | null;
  icon?: string | null;
}

export interface CategoryTagProps {
  category: CategoryInfo | string | Record<string, unknown> | null | undefined;
  /**
   * カテゴリをクリックした時にリンクするページのパス
   * 例: '/app/equipments', '/app/parts', '/app/suppliers', '/app/schedule'
   */
  linkPath?: string;
  /**
   * リンクのクエリパラメータ名（デフォルト: 'category'）
   */
  linkParamName?: string;
  /**
   * カテゴリマップ（文字列IDの場合に使用）
   */
  categoryMap?: Record<string, string>;
  /**
   * クリック可能にするか（デフォルト: linkPathが指定されている場合true）
   */
  clickable?: boolean;
}

/**
 * カテゴリを統一的に表示する共通コンポーネント
 *
 * 機能:
 * - アイコン表示
 * - 色表示
 * - カテゴリ名表示
 * - クリックでフィルタされた一覧へリンク
 *
 * 使用例:
 * ```tsx
 * // 設備カテゴリを表示し、クリックで設備一覧（カテゴリフィルタ）へ遷移
 * <CategoryTag
 *   category={equipment.category}
 *   linkPath="/app/equipments"
 * />
 *
 * // 部品・消耗品カテゴリを表示（リンクなし）
 * <CategoryTag category={part.category} />
 *
 * // カテゴリIDが文字列の場合（categoryMapで名前を取得）
 * <CategoryTag
 *   category={equipment.category}
 *   categoryMap={categoryMap}
 *   linkPath="/app/equipments"
 * />
 * ```
 */
export const CategoryTag: React.FC<CategoryTagProps> = ({
  category,
  linkPath,
  linkParamName = 'category',
  categoryMap,
  clickable,
}) => {
  const navigate = useNavigate();
  if (!category) {
    return <span>-</span>;
  }

  let categoryInfo: CategoryInfo | null = null;
  let categoryId: string | null = null;

  // カテゴリがオブジェクトの場合
  if (typeof category === 'object' && category !== null) {
    const record = category as Record<string, unknown>;
    const recordId = typeof record.id === 'string' ? record.id : null;
    const recordLabel =
      typeof record.categoryLabel === 'string'
        ? record.categoryLabel
        : typeof record.label === 'string'
          ? record.label
          : typeof record.name === 'string'
            ? record.name
            : typeof record.title === 'string'
              ? record.title
              : recordId;

    categoryId = recordId;
    categoryInfo = {
      id: recordId ?? (recordLabel ?? '-'),
      categoryLabel: recordLabel ?? recordId ?? '-',
      color: typeof record.color === 'string' ? record.color : null,
      icon: typeof record.icon === 'string' ? record.icon : null,
    };
  }
  // カテゴリが文字列ID の場合
  else if (typeof category === 'string') {
    categoryId = category;
    const categoryName = categoryMap?.[categoryId] || categoryId;
    categoryInfo = {
      id: categoryId,
      categoryLabel: categoryName,
      color: null,
      icon: null,
    };
  }

  if (!categoryInfo) {
    return <span>-</span>;
  }

  // アイコンコンポーネントを取得
  const IconComponent = categoryInfo.icon
    ? (Icons[categoryInfo.icon as keyof typeof Icons] as React.ComponentType<{ style?: React.CSSProperties }> | null)
    : null;
  // Tagの内容
  const tagContent = (
    <>
      {IconComponent && <IconComponent style={{ marginRight: 4 }} />}
      {categoryInfo.categoryLabel}
    </>
  );

  // リンク可能かどうか
  const isClickable = clickable !== undefined ? clickable : !!linkPath;

  // クリックハンドラー
  const handleClick = (e: React.MouseEvent) => {
    if (isClickable && linkPath && categoryId) {
      e.stopPropagation(); // イベントの伝播を停止
      navigate(`${linkPath}?${linkParamName}=${categoryId}`);
    }
  };

  if (isClickable && linkPath && categoryId) {
    // クリック可能な場合
    return (
      <Tag
        color={categoryInfo.color || undefined}
        style={{ cursor: 'pointer' }}
        onClick={handleClick}
      >
        {tagContent}
      </Tag>
    );
  }

  // リンクなしの場合
  return (
    <Tag color={categoryInfo.color || undefined}>
      {tagContent}
    </Tag>
  );
};

/**
 * 複数のカテゴリを表示するコンポーネント
 */
export interface CategoryTagsProps {
  categories: (CategoryInfo | string)[];
  linkPath?: string;
  linkParamName?: string;
  categoryMap?: Record<string, string>;
}

export const CategoryTags: React.FC<CategoryTagsProps> = ({
  categories,
  linkPath,
  linkParamName,
  categoryMap,
}) => {
  if (!categories || categories.length === 0) {
    return <span>-</span>;
  }

  return (
    <>
      {categories.map((cat, index) => {
        const categoryId = typeof cat === 'string' ? cat : cat.id;
        return (
          <CategoryTag
            key={categoryId || index}
            category={cat}
            linkPath={linkPath}
            linkParamName={linkParamName}
            categoryMap={categoryMap}
          />
        );
      })}
    </>
  );
};
